import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, User } from '@/types/user';
import * as api from '@/lib/api';

const initialState: AuthState = {
  token: api.getToken(),
  user: null,
  isAuthenticated: !!api.getToken(),
  isLoading: false,
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ phone, code }: { phone: string; code: string }) => {
    const response = await api.auth(phone, code);
    api.setToken(response.access_token);
    
    // OpenIdConnect токены не декодируются как JWT
    // Используем getUserByPhone для получения userId (как в мобильном приложении)
    let userId: number | null = null;
    let user = null;
    
    try {
      // Получаем пользователя по номеру телефона
      const userDataFromPhone = await api.getUserByPhone(phone);
      userId = userDataFromPhone?.id;
      
      if (userId) {
        api.setUserId(userId);
        // Загружаем полные данные пользователя
        user = await api.getUser(userId);
      }
    } catch (error) {
      console.warn('Не удалось получить данные пользователя:', error);
      // Пытаемся извлечь userId из ответа авторизации
      userId = response.userId;
      if (userId) {
        api.setUserId(userId);
        try {
          user = await api.getUser(userId);
        } catch (e) {
          console.warn('Не удалось загрузить данные пользователя по ID:', e);
        }
      }
    }
    
    return { token: response.access_token, user };
  }
);

export const loginWithPassword = createAsyncThunk(
  'auth/loginWithPassword',
  async ({ phone, password }: { phone: string; password: string }) => {
    const response = await api.authWithPassword(phone, password);
    api.setToken(response.access_token);
    
    // OpenIdConnect токены не декодируются как JWT
    // Используем getUserByPhone для получения userId (как в мобильном приложении)
    let userId: number | null = null;
    let user = null;
    
    try {
      // Получаем пользователя по номеру телефона
      const userDataFromPhone = await api.getUserByPhone(phone);
      userId = userDataFromPhone?.id;
      
      if (userId) {
        api.setUserId(userId);
        // Загружаем полные данные пользователя
        user = await api.getUser(userId);
      }
    } catch (error) {
      console.warn('Не удалось получить данные пользователя:', error);
      // Пытаемся извлечь userId из ответа авторизации
      userId = response.userId;
      if (userId) {
        api.setUserId(userId);
        try {
          user = await api.getUser(userId);
        } catch (e) {
          console.warn('Не удалось загрузить данные пользователя по ID:', e);
        }
      }
    }
    
    return { token: response.access_token, user };
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  api.removeToken();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
      })
      .addCase(loginWithPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginWithPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(loginWithPassword.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      });
  },
});

export const { setUser } = authSlice.actions;
export default authSlice.reducer;

